import type { AudioTap, MediaStreamLike } from "@cuekards/ui";
import {
  MIC_PROCESSOR,
  MIC_SAMPLE_RATE,
  type MicMessage,
} from "@cuekards/core/speech";

/**
 * The microphone fork, in a tab (FR-31) — the web's implementation of
 * `@cuekards/ui`'s {@link AudioTap}, and the one piece of the capture path that is
 * not shared between the two hosts.
 *
 * `CaptureEngine` opens the device exactly once. This attaches the *second*
 * consumer to that same open stream and streams 16 kHz mono frames out. That is
 * the whole of what makes one open sufficient, and it is why the invariant is
 * structural rather than a rule two modules have to remember: there is one
 * `getUserMedia` in the shared package, and the follower's audio comes off its
 * stream rather than out of a second one. Opening the device twice risks
 * exclusive-mode contention and drift between the streams, and the failure would
 * show up as damaged audio in a take that cannot be redone (risk R3).
 *
 * Three things it is careful about, all in service of "recorded audio is
 * byte-for-byte unaffected by the fork":
 *
 * - **It observes, it does not intervene.** A `MediaStreamAudioSourceNode` reads
 *   the existing track; it does not clone it, re-open the device, or sit between
 *   the track and the `MediaRecorder`. The recorder is wired to the
 *   `MediaStream` and never sees this graph at all.
 * - **Nothing reaches the output.** The worklet node is connected to nothing, so
 *   no audio is routed back to the speakers — which on a filming machine would
 *   be feedback into the take.
 * - **The graph is torn down with the stream.** A context left running holds the
 *   audio hardware awake and, on some Chromium builds, the tracks with it.
 *
 * **It is a worklet and not a `ScriptProcessorNode`**, and since one-window
 * ticket 04 so is the desktop's. The trade used to go the other way there: a
 * worklet needed a second entry point in a three-target Electron build, to save
 * eight wake-ups a second on a thread that was not doing much — the desktop's
 * microphone lived in the control window and its prompter in another one. One
 * window puts them on the same thread, so both hosts now have a main thread
 * painting a scrolling prompter of one span per word, where a dropped buffer is
 * a dropped word.
 *
 * Where the frames *go* is `useCapture`'s business and the recogniser is web
 * ticket 14's; until it exists they reach a worker that has nothing to do with
 * them, which is a send nobody handles rather than a fork that is not there.
 */
export const attachAudioTap: AudioTap = (
  stream: MediaStreamLike,
  onFrame: (samples: Float32Array) => void,
): (() => void) => {
  // Asking for the model's rate is how the resample happens: the browser
  // converts the 44.1/48 kHz microphone into the graph in native code, so
  // nothing here has to.
  const context = new AudioContext({ sampleRate: MIC_SAMPLE_RATE });
  let detached = false;
  let teardown: (() => void) | null = null;

  void (async () => {
    try {
      await context.audioWorklet.addModule(workletUrl());
      // Detached while the module was loading — a take that ended almost as soon
      // as it began. Nothing was attached, so there is only the context to close.
      if (detached) return;

      // Mono in, and no outputs at all. The recogniser takes one channel and a
      // webcam microphone is usually mono anyway; declaring zero outputs is what
      // makes "nothing reaches the speakers" structural rather than a rule about
      // what this function remembers not to connect. Same options on both hosts.
      const source = context.createMediaStreamSource(stream as MediaStream);
      const node = new AudioWorkletNode(context, MIC_PROCESSOR, {
        numberOfInputs: 1,
        numberOfOutputs: 0,
        channelCount: 1,
      });
      node.port.onmessage = (event: MessageEvent<MicMessage>) => {
        const message = event.data;
        if (message.kind === "frame") onFrame(message.samples);
        else if (message.sampleRate !== MIC_SAMPLE_RATE) {
          console.warn(
            `cuekards: the microphone fork is running at ${message.sampleRate} Hz, not ${MIC_SAMPLE_RATE}`,
          );
        }
      };
      // Deliberately not connected onward. A worklet runs whether or not
      // anything is downstream of it, unlike the `ScriptProcessorNode` this
      // replaced, so no zero-gain sink is needed to keep it firing.
      source.connect(node);

      teardown = () => {
        node.port.onmessage = null;
        source.disconnect();
        node.disconnect();
      };
    } catch (error) {
      // The worklet could not be loaded. It costs the follower, not the take:
      // the recorder is on the stream and untouched, and nothing here is in its
      // path.
      console.error("cuekards: the microphone fork could not start", error);
    }
  })();

  // Idempotent, because it can genuinely be called twice: a recorder that errors
  // and then stops reports both, and closing a closed context rejects.
  return () => {
    if (detached) return;
    detached = true;
    teardown?.();
    void context.close().catch(() => {
      // Already closing. Nothing left to do about it.
    });
  };
};

/**
 * Where the worklet module is.
 *
 * At the **site root**, and rooted rather than relative — which since seo ticket
 * 04 is a correctness requirement rather than a preference. The application is
 * served at `/app/`, and Cloudflare's `auto-trailing-slash` means that slash is
 * really there: a relative `mic-worklet.js` resolved against `document.baseURI`
 * asks for `/app/mic-worklet.js`, where the build writes nothing.
 *
 * That failure is worth stating because of how quietly it lands. `addModule`
 * rejects, the rejection is caught and logged by the caller, and the page carries
 * on with no microphone fork — so speech-following and the level meter are both
 * gone with nothing on screen saying why. It is the same reason `index.html`'s
 * own links are absolute.
 *
 * Still resolved against the document rather than from this module: the audio
 * thread loads it by URL, and the bundle it would otherwise be resolved against
 * is the one it is deliberately not part of. A rooted path makes the document's
 * own address irrelevant, which is the whole point.
 */
function workletUrl(): string {
  return new URL("/mic-worklet.js", document.baseURI).href;
}
